import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { SbButton, SbSelect, SearchOutlined, SbTooltip, TooltipTriggerStyled } from "uiCore";
import SbTable from "uiCore/SbTable/SbTable";
import { createJobCode, hideToast } from "Store/actions/profileListActions";
import ToasterModal from "Components/SbErrorToasterComponent/SbErrorToaster";
import SbAdminJobCodeDrawer from "Components/SbAdminJobCodeDrawer/SbAdminJobCodeDrawer";
import { getJobView } from "Api/apiService";
import { DEEP_LINK_ACTIONS } from "Utils/deepLinks";
import useDeepLinkAction from "Hooks/useDeepLinkAction";
import useAuthStore from "Store/useAuthStore";
import { SearchWrapperStyled } from "Pages/JobsLibraryV2/JobsLibraryV2.styles";
import { TableWrapperStyled } from "styles/common.styles";
import {
  PageContainer,
  HeaderSection,
  HeaderLeft,
  BackButton,
  TitleGroup,
  PageTitle,
  PageSubtitle,
} from "../CompensationManagement/CompensationManagement.styles";
import { JobCodesHeaderRight, JobCodesTableWrapperStyled } from "./AdminJobCodesPage.styles";

const JOB_CODE_SEARCH_FIELDS = ["job_code", "job_title", "legal_entity", "business_unit", "role_type", "job_level"];

// PRD §5.17 — tooltip content strings for Admin Job Codes page
const TOOLTIP_ADD_JOB_CODE = "Create a new job code — the master reference for a role type in your organisation.";
const TOOLTIP_JOB_CODE_SEARCH = "Search job codes by code, title, business unit, or legal entity.";

const AdminJobCodesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const { show: showToast, message: toastMessage, type: toastType } = useSelector((s) => s.profileList.toast);

  const [isJobCodeDrawerOpen, setIsJobCodeDrawerOpen] = useState(false);
  const [jobCodeSaving, setJobCodeSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobCodeData, setJobCodeData] = useState([]);
  const [pageSearchTerm, setPageSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ── Deep link: /admin-setup/job-codes?action=create-job-code ───────────────
  // The knowledge base documents the fields INSIDE this drawer, so an answer
  // about them should open it rather than leave the user on the table. The route
  // is already gated by EntitlementGuard, so reaching this page is the permission
  // check — same as the Add Job Code button beside the table.
  useDeepLinkAction({
    action: DEEP_LINK_ACTIONS.CREATE_JOB_CODE,
    onAllowed: () => setIsJobCodeDrawerOpen(true),
  });

  const outerFields = useMemo(
    () =>
      orgMetaData?.ui_elements
        ?.find((el) => el.type === "job_code")
        ?.field_details?.fields?.slice(0, 6) ?? [],
    [orgMetaData],
  );

  const fetchJobCodes = async () => {
    setLoading(true);
    try {
      const data = await getJobView(email);
      setJobCodeData(Array.isArray(data) ? [...data].reverse() : []);
    } catch {
      setJobCodeData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobCodes();
  }, [refreshTrigger]);

  useEffect(() => {
    if (!jobCodeData.length) { setSearchOptions([]); return; }
    const unique = new Set();
    jobCodeData.forEach((row) =>
      JOB_CODE_SEARCH_FIELDS.forEach((f) => {
        const v = row[f];
        if (v && typeof v === "string" && v.trim()) unique.add(v);
      }),
    );
    setSearchOptions(Array.from(unique).map((v) => ({ value: v, label: v })));
  }, [jobCodeData]);

  const filteredData = useMemo(() => {
    const term = (pageSearchTerm ?? "").trim().toLowerCase();
    if (!term) return jobCodeData;
    return jobCodeData.filter((row) =>
      JOB_CODE_SEARCH_FIELDS.some((f) => row[f]?.toString().toLowerCase().includes(term)),
    );
  }, [jobCodeData, pageSearchTerm]);

  const getFilters = (key) =>
    [...new Set((filteredData || []).map((r) => r[key]).filter(Boolean))].map((v) => ({
      text: v,
      value: v,
    }));

  const columns = useMemo(() => {
    const fieldDefs = [
      { dataIndex: "job_code",      fieldIdx: 0 },
      { dataIndex: "job_title",     label: "Job Title" },
      { dataIndex: "legal_entity",  fieldIdx: 1 },
      { dataIndex: "business_unit", fieldIdx: 2 },
      { dataIndex: "role_type",  fieldIdx: 3 },
      { dataIndex: "job_level", fieldIdx: 4 },
    ];

    return fieldDefs
      .map(({ dataIndex, fieldIdx, label }) => {
        const title = fieldIdx !== undefined
          ? (outerFields[fieldIdx]?.label ?? label ?? dataIndex)
          : label ?? dataIndex;
        return {
          title,
          dataIndex,
          filters: getFilters(dataIndex),
          onFilter: (value, record) => record[dataIndex] === value,
        };
      });
  }, [outerFields, filteredData]);

  return (
    <>
    <ToasterModal
      show={showToast}
      message={toastMessage}
      type={toastType}
      onClose={() => dispatch(hideToast())}
    />
    <PageContainer>
      <HeaderSection>
        <HeaderLeft>
          <BackButton onClick={() => navigate("/admin-setup")}>
            <ArrowLeftOutlined />
          </BackButton>
          <TitleGroup>
            <PageTitle>All Job Codes</PageTitle>
              <PageSubtitle>Create and view job codes for your organization</PageSubtitle>
          </TitleGroup>
        </HeaderLeft>

        <JobCodesHeaderRight>
          <SbTooltip content={TOOLTIP_JOB_CODE_SEARCH}>
            <SearchWrapperStyled>
              <SbSelect
                options={searchOptions}
                value={pageSearchTerm || undefined}
                suffixIcon={
                  <SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />
                }
                onChange={(v) => setPageSearchTerm(v ?? "")}
                placeholder="Search by job code, title…"
                allowClear
                showSearch
                notFoundContent="No matching results"
                listHeight={300}
              />
            </SearchWrapperStyled>
          </SbTooltip>
          <SbTooltip content={TOOLTIP_ADD_JOB_CODE}>
            <TooltipTriggerStyled>
              <SbButton
                buttonType="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsJobCodeDrawerOpen(true)}
              >
                Add Job Code
              </SbButton>
            </TooltipTriggerStyled>
          </SbTooltip>
        </JobCodesHeaderRight>
      </HeaderSection>

      <TableWrapperStyled>
        <JobCodesTableWrapperStyled>
          <SbTable
            columns={columns}
            dataSource={filteredData}
            rowKey={(r, idx) => r.job_code ?? idx}
            loading={loading}
            sticky
            onRow={() => ({ style: { cursor: "default" } })}
          />
        </JobCodesTableWrapperStyled>
      </TableWrapperStyled>

      {isJobCodeDrawerOpen && (
        <SbAdminJobCodeDrawer
          isOpen={isJobCodeDrawerOpen}
          saving={jobCodeSaving}
          onClose={() => setIsJobCodeDrawerOpen(false)}
          onSave={async (apiPayload) => {
            try {
              setJobCodeSaving(true);
              const result = await dispatch(createJobCode(apiPayload));
              if (result) setRefreshTrigger((t) => t + 1);
              return result;
            } finally {
              setJobCodeSaving(false);
            }
          }}
        />
      )}
    </PageContainer>
    </>
  );
};

export default AdminJobCodesPage;
